import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export interface BrregData {
  navn: string;
  organisasjonsnummer: string;
  stiftelsesdato: string;
  antallAnsatte: number;
  forretningsadresse: {
    adresse: string[];
    postnummer: string;
    poststed: string;
    kommune: string;
  };
  naeringskode1: {
    beskrivelse: string;
    kode: string;
  };
}

export interface FinancialData {
  id?: number;
  journalnr?: string;
  regnskapstype?: string;
  virksomhet?: {
    organisasjonsnummer: string;
    organisasjonsform?: string;
    morselskap?: boolean;
  };
  egenkapitalGjeld?: {
    sumEgenkapitalGjeld: number;
    egenkapital?: {
      sumEgenkapital: number;
      opptjentEgenkapital?: {
        sumOpptjentEgenkapital: number;
      };
      innskuttEgenkapital?: {
        sumInnskuttEgenkaptial: number;
      };
    };
    gjeldOversikt?: {
      sumGjeld: number;
      kortsiktigGjeld?: {
        sumKortsiktigGjeld: number;
      };
      langsiktigGjeld?: {
        sumLangsiktigGjeld: number;
      };
    };
  };
  eiendeler?: {
    sumEiendeler: number;
    omloepsmidler?: {
      sumOmloepsmidler: number;
    };
    anleggsmidler?: {
      sumAnleggsmidler: number;
    };
  };
  resultatregnskapResultat?: {
    ordinaertResultatFoerSkattekostnad?: number;
    aarsresultat?: number;
    finansresultat?: {
      nettoFinans?: number;
      finansinntekt?: {
        sumFinansinntekter?: number;
      };
      finanskostnad?: {
        sumFinanskostnad?: number;
      };
    };
    driftsresultat?: {
      driftsresultat?: number;
      driftsinntekter?: {
        sumDriftsinntekter?: number;
      };
      driftskostnad?: {
        sumDriftskostnad?: number;
      };
    };
  };
  regnskapsperiode?: {
    fraDato: string;
    tilDato: string;
  };
  valuta?: string;
}

export interface BrregDataResult {
  data: BrregData | null;
  isLoading: boolean;
  error: string | null;
}

export interface FinancialDataResult {
  data: FinancialData[] | null;
  isLoading: boolean;
  error: string | null;
}

const NOT_FOUND = 404;

export async function fetchBrregData(orgNumber: string): Promise<BrregData | null> {
  const cleanOrgNumber = orgNumber.replace(/\s+/g, "");
  const response = await fetch(`https://data.brreg.no/enhetsregisteret/api/enheter/${cleanOrgNumber}`);

  if (response.status === NOT_FOUND) return null;
  if (!response.ok) {
    throw new Error(`Failed to fetch company data: ${response.status}`);
  }

  return await response.json();
}

export const fetchFinancialData = async (orgNumber: string): Promise<FinancialData[]> => {
  const cleanOrgNumber = orgNumber.replace(/\s+/g, "");
  // www. 301-redirects here without CORS headers on the redirect, which fails the fetch in browsers.
  const url = `https://aksjegrafen.com/brreg/financials?orgnr=${cleanOrgNumber}`;
  return fetch(url).then((response) => {
    if (response.status === NOT_FOUND) return [];
    if (!response.ok) {
      throw new Error(`Failed to fetch financial data: ${response.status}`);
    }
    return response.json();
  });
};

export function useBrregData(
  organizationNumber: string | undefined | null,
  countryCode: string | undefined
): BrregDataResult {
  const [data, setData] = useState<BrregData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const getBrregData = async () => {
      if (organizationNumber && countryCode === "NOR") {
        setIsLoading(true);
        setError(null);

        try {
          const result = await fetchBrregData(organizationNumber);
          setData(result);
        } catch (err) {
          console.error("Error fetching from Brønnøysundregistrene:", err);
          setError(t("Could not fetch company details"));
          setData(null);
        } finally {
          setIsLoading(false);
        }
      } else {
        setData(null);
        setError(null);
      }
    };

    getBrregData();
  }, [organizationNumber, countryCode, t]);

  return { data, isLoading, error };
}

export function useFinancialData(
  organizationNumber: string | undefined | null,
  countryCode: string | undefined
): FinancialDataResult {
  const [data, setData] = useState<FinancialData[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const getFinancialData = async () => {
      if (organizationNumber && countryCode === "NOR") {
        setIsLoading(true);
        setError(null);

        try {
          const result = await fetchFinancialData(organizationNumber);
          setData(result);
        } catch (err) {
          console.error("Error fetching financial data:", err);
          setError(t("Could not fetch financial data"));
          setData(null);
        } finally {
          setIsLoading(false);
        }
      } else {
        setData(null);
        setError(null);
      }
    };

    getFinancialData();
  }, [organizationNumber, countryCode, t]);

  return { data, isLoading, error };
}
